/**
 * Two-click placement for `canvasConnector` nodes (tail then head), with live ghost line.
 * Edge arrow preset is applied from toolbar shape stroke settings for consistency.
 */

import { Node, useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CANVAS_SNAP } from "../canvas-controls.config";
import { useCanvas } from "../context/CanvasContext";
import { SHAPE_COLORS } from "../nodes/shared/CanvasColorPalettes";
import { normalizeCanvasStrokeWidth } from "../types/canvasNodeData";
import { useCanvasShapeAndEdgeStyle } from "./useCanvasShapeAndEdgeStyle";

const snapToGrid = (val: number, step: number) => Math.round(val / step) * step;

export interface ConnectorPreset {
  /** "none" maps to no arrows; "end" maps to end_arrow: "arrow"; "both" maps to both. */
  arrowMode: "none" | "end" | "both";
  curve_type: "straight" | "smooth" | "step";
}

/**
 * Ghost shape while placing a connector.
 * When `tail` is null the user hasn't clicked yet.
 * When `tail` is set the user has placed the start point and is aiming at the head.
 */
export interface ConnectorPlacementGhost {
  tail: { x: number; y: number } | null;
  head: { x: number; y: number };
}

export function useConnectorPlacement() {
  const { screenToFlowPosition } = useReactFlow();
  const { addNode } = useCanvas();
  const { activeShapeColor, activeShapeStrokeStyle, activeShapeStrokeWidth } =
    useCanvasShapeAndEdgeStyle();
  const activeShapeStrokeColor =
    SHAPE_COLORS.find((c) => c.value === activeShapeColor)?.swatchHex ??
    activeShapeColor;

  const [isPlacingConnector, setIsPlacingConnector] = useState(false);
  const [ghost, setGhost] = useState<ConnectorPlacementGhost | null>(null);

  const screenToFlowRef = useRef(screenToFlowPosition);
  screenToFlowRef.current = screenToFlowPosition;

  const [snapX, snapY] = CANVAS_SNAP.grid;
  const activePresetRef = useRef<ConnectorPreset>({
    arrowMode: "end",
    curve_type: "straight",
  });

  const startPlacing = useCallback((preset: ConnectorPreset) => {
    activePresetRef.current = preset;
    setIsPlacingConnector(true);
    setGhost(null);
  }, []);

  const cancelPlacing = useCallback(() => {
    setIsPlacingConnector(false);
    setGhost(null);
  }, []);

  useEffect(() => {
    if (!isPlacingConnector) {
      setGhost(null);
      return;
    }

    // Phase 1: waiting for tail click. Phase 2: tail is set, waiting for head.
    let tail: { x: number; y: number } | null = null;
    // Track mousedown position to distinguish drag from click-click
    let mouseDownScreen: { x: number; y: number } | null = null;
    let isDragging = false;

    const getFlow = (e: MouseEvent | PointerEvent) =>
      screenToFlowRef.current({ x: e.clientX, y: e.clientY });

    const snap = (pos: { x: number; y: number }) => ({
      x: snapToGrid(pos.x, snapX),
      y: snapToGrid(pos.y, snapY),
    });

    const isOnPane = (e: MouseEvent | PointerEvent): boolean => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-canvas-control]")) return false;
      return !!target.closest(".react-flow__pane");
    };

    const commit = (x1: number, y1: number, x2: number, y2: number) => {
      const minX = Math.min(x1, x2);
      const minY = Math.min(y1, y2);
      // Add padding so thin connectors (near-horizontal/vertical) have a
      // reasonable node bounding box and the arrowheads aren't clipped.
      const PAD = 20;
      const width = Math.max(Math.abs(x2 - x1), 1) + PAD * 2;
      const height = Math.max(Math.abs(y2 - y1), 1) + PAD * 2;

      const newNode: Node = {
        id: uuidv4(),
        type: "canvasConnector",
        position: { x: minX - PAD, y: minY - PAD },
        width,
        height,
        data: {
          start: { x: x1 - minX + PAD, y: y1 - minY + PAD },
          end: { x: x2 - minX + PAD, y: y2 - minY + PAD },
          color: activeShapeStrokeColor,
          stroke_width: normalizeCanvasStrokeWidth(activeShapeStrokeWidth),
          stroke_style: activeShapeStrokeStyle,
          start_arrow:
            activePresetRef.current.arrowMode === "both" ? "arrow" : "none",
          end_arrow:
            activePresetRef.current.arrowMode !== "none" ? "arrow" : "none",
          curve_type: activePresetRef.current.curve_type,
        },
      };

      addNode(newNode);
      tail = null;
      isDragging = false;
      mouseDownScreen = null;
      setIsPlacingConnector(false);
      setGhost(null);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelPlacing();
    };

    // Suppress RF drag-to-select and panning on the pane while placing
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 || !isOnPane(e)) return;
      e.stopPropagation();
    };
    const onPointerUp = (e: PointerEvent) => {
      if (e.button !== 0 || !isOnPane(e)) return;
      e.stopPropagation();
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0 || !isOnPane(e)) return;
      e.preventDefault();
      e.stopPropagation();
      mouseDownScreen = { x: e.clientX, y: e.clientY };
      isDragging = false;

      const pos = snap(getFlow(e));

      if (!tail) {
        // Phase 1: place the tail
        tail = pos;
        setGhost({ tail, head: pos });
      }
      // Phase 2: mousedown with tail already set — could be start of drag
    };

    const onMouseMove = (e: MouseEvent) => {
      const pos = snap(getFlow(e));

      if (mouseDownScreen && e.buttons === 1) {
        const dx = e.clientX - mouseDownScreen.x;
        const dy = e.clientY - mouseDownScreen.y;
        if (Math.abs(dx) >= 4 || Math.abs(dy) >= 4) isDragging = true;
      }

      if (!tail) {
        // No tail yet — no ghost to show (per spec: no ghost preview needed)
        return;
      }

      // Tail is set — show live preview to current cursor
      setGhost({ tail, head: pos });
    };

    const onMouseUp = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (!mouseDownScreen) return;

      const pos = snap(getFlow(e));

      if (!tail) {
        // mouseup without prior mousedown on pane — ignore
        mouseDownScreen = null;
        return;
      }

      if (isDragging) {
        // Drag gesture: mousedown set the tail, mouseup places the head
        commit(tail.x, tail.y, pos.x, pos.y);
        mouseDownScreen = null;
        return;
      }

      // Click-click mode: mouseup without significant drag
      // If the tail was just placed on this mousedown (same coords), wait for next click
      if (pos.x === tail.x && pos.y === tail.y) {
        // Just placed the tail, stay in phase 2 and wait for next click
        mouseDownScreen = null;
        return;
      }

      // Second click at a different position → place head here
      commit(tail.x, tail.y, pos.x, pos.y);
      mouseDownScreen = null;
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    window.addEventListener("pointerup", onPointerUp, { capture: true });
    window.addEventListener("mousedown", onMouseDown, { capture: true });
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp, { capture: true });

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
      window.removeEventListener("pointerup", onPointerUp, { capture: true });
      window.removeEventListener("mousedown", onMouseDown, { capture: true });
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp, { capture: true });
    };
  }, [
    isPlacingConnector,
    cancelPlacing,
    snapX,
    snapY,
    addNode,
    activeShapeStrokeColor,
    activeShapeStrokeStyle,
    activeShapeStrokeWidth,
  ]);

  return { isPlacingConnector, ghost, startPlacing, cancelPlacing };
}
